import React, { memo, useContext } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { useRoutes } from 'react-router-dom';
import { styled } from '@mui/material';

import AppContext from 'app/AppContext';
import FuseDialog from '@fuse/core/FuseDialog';
import FuseMessage from '@fuse/core/FuseMessage';
import FuseSuspense from '@fuse/core/FuseSuspense';
import SettingsPanel from 'app/fuse-layouts/shared-components/SettingsPanel';

import FooterLayout3 from './components/FooterLayout3';
import LeftSideLayout3 from './components/LeftSideLayout3';
import NavbarWrapperLayout3 from './components/NavbarWrapperLayout3';
import RightSideLayout3 from './components/RightSideLayout3';
import ToolbarLayout3 from './components/ToolbarLayout3';

interface RootProps {
	mode: 'boxed' | 'container';
	containerWidth: number;
}

const Root = styled('div')<RootProps>(({ containerWidth, mode }) => ({
	...(mode === 'boxed' && {
		clipPath: 'inset(0)',
		maxWidth: `${containerWidth}px`,
		margin: '0 auto',
		boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
	}),
	...(mode === 'container' && {
		'& .container': {
			maxWidth: `${containerWidth}px`,
			width: '100%',
			margin: '0 auto'
		}
	})
}));

function Layout3({ children }: { children: React.ReactNode }) {
	const config = useSelector(({ fuse }: any) => fuse.settings.current.layout.config);
	const appContext = useContext(AppContext);
	const { routes } = appContext;
	return (
		<Root id="fuse-layout" className="flex w-full" containerWidth={config.containerWidth} mode={config.mode}>
			{config.leftSidePanel.display && <LeftSideLayout3 />}

			<div className="flex min-w-0 flex-auto flex-col">
				<main id="fuse-main" className="relative flex min-h-screen min-w-0 flex-auto flex-col">
					{config.navbar.display && (
						<NavbarWrapperLayout3
							className={clsx(config.navbar.style === 'fixed' && 'sticky top-0 z-50')}
						/>
					)}

					{config.toolbar.display && (
						<ToolbarLayout3
							className={clsx(
								config.toolbar.style === 'fixed' && 'sticky top-0',
								config.toolbar.position === 'above' && 'z-40 order-first'
							)}
						/>
					)}

					<div className="sticky top-0 z-99">
						<SettingsPanel />
					</div>

					<div className="relative z-10 flex min-h-0 flex-auto flex-col">
						<FuseDialog />

						<FuseSuspense>{useRoutes(routes)}</FuseSuspense>

						{children}
					</div>

					{config.footer.display && (
						<FooterLayout3 className={config.footer.style === 'fixed' ? 'sticky bottom-0' : ''} />
					)}
				</main>
			</div>

			{config.rightSidePanel.display && <RightSideLayout3 />}
			<FuseMessage />
		</Root>
	);
}

export default React.memo(Layout3);
